import React from "react";
import {
  IX10ConfigCommand,
  IX10ConfigCommandItem,
  X10ConfigKey
} from "../../../../../store/x10config/types";
import { HistoryTable } from "./configuration.styles";
import { formatLiters, formatOffset } from "./helpers";
import { formatDateTime, StatusChip } from "./SyncStatus";
import useConfigurationText from "./useConfigurationText";

const KEY_LABELS: { [key in X10ConfigKey]: [string, string] } = {
  V_OFFSET: ["vessel_offset", "Vessel probe offset"],
  A_OFFSET: ["ambient_offset", "Ambient probe offset"],
  HALO: ["halo", "Halo light"],
  V_FAIL_MODE: ["fail_mode", "Cooling fail mode"],
  VOLUME_LEFT: ["volume", "Keg volume"]
};

const SOURCE_LABELS: { [source: string]: [string, string] } = {
  CONTROL_TOWER: ["source_control_tower", "Control Tower"],
  X10_APP: ["source_x10_app", "the owner app"],
  BOOTUP_RESYNC: ["source_bootup", "re-sent after a restart"],
  HEARTBEAT_RESYNC: ["source_heartbeat", "re-sent when the unit was back"]
};

const ConfigurationHistory: React.FC<{ history: IX10ConfigCommand[] | undefined }> = ({
  history
}) => {
  const t = useConfigurationText();

  const formatValue = (item: IX10ConfigCommandItem) => {
    switch (item.key) {
      case "V_OFFSET":
      case "A_OFFSET":
        return formatOffset(item.value as number);
      case "VOLUME_LEFT":
        return formatLiters(item.value as number);
      default:
        return item.value ? t("on", "On") : t("off", "Off");
    }
  };

  if (!history?.length) {
    return <p className="text-grey-grey100 text-sm">{t("history_empty", "No changes yet.")}</p>;
  }

  return (
    <HistoryTable>
      <thead>
        <tr>
          <th>{t("history_when", "When")}</th>
          <th>{t("history_by", "By")}</th>
          <th>{t("history_change", "Change")}</th>
          <th>{t("history_status", "Status")}</th>
        </tr>
      </thead>
      <tbody>
        {history.map(command => {
          const [sourceKey, sourceFallback] = SOURCE_LABELS[command.source] || [
            command.source,
            command.source
          ];
          return command.items.map((item, index) => (
            <tr key={`${command.id}-${item.key}`}>
              <td>{index === 0 ? formatDateTime(command.sentAt || command.requestedAt) : ""}</td>
              <td>
                {index === 0
                  ? command.source === "CONTROL_TOWER"
                    ? command.requestedBy || t(sourceKey, sourceFallback)
                    : t(sourceKey, sourceFallback)
                  : ""}
              </td>
              <td>{`${t(...KEY_LABELS[item.key])}: ${formatValue(item)}`}</td>
              <td>
                <StatusChip configKey={item.key} status={item.status} />
                {index === 0 && command.statusDetail && (
                  <div className="text-grey-grey100 text-sm pt-xxs">{command.statusDetail}</div>
                )}
              </td>
            </tr>
          ));
        })}
      </tbody>
    </HistoryTable>
  );
};

export default ConfigurationHistory;
